import { Injectable } from '@angular/core';

export type AuditMapStatusFilter =
  | 'on'
  | 'off'
  | '6hours'
  | '24hours'
  | 'never'
  | null
  | undefined
  | string;

export interface AuditMapFilterFormValue {
  reference?: string | null;
  magneticKey?: boolean | null;
  gatewayStatus?: string | null;
  status?: AuditMapStatusFilter;
  division?: Array<string | number> | null;
  gateway?: Array<string | number> | null;
  id?: Array<string | number> | null;
  street?: string | null;
  district?: string | null;
  latitude?: any;
  longitude?: any;
  serialNumber?: string | null;
  circuitBox?: string | null;
}

export interface BuildGeoserverUrlResult {
  url: string;
  gatewayIdsToShow?: Array<string | number>;
}

export interface FilterByStatusResult<T = any> {
  filtered: T[];
  installationsTransmissionRoute: T[];
}

@Injectable({ providedIn: 'root' })
export class AuditMapFilterService {
  buildGeoserverURL(value: AuditMapFilterFormValue): BuildGeoserverUrlResult {
    // URL utilizada no filtro(Geoserver)
    let url = `is_active=true`;

    // Verifica se o campo sub dibvisões foi utilizado
    if (value.division && value.division.length) {
      url += `%20and%20division_id%20IN%20(${value.division.join(',%20')})`;
    }

    // Verifica se o campo Concentrador foi utilizado
    if (value.gateway && value.gateway.length) {
      url += `%20and%20gateway_id%20IN%20(${value.gateway.join(',%20')})`;
    }

    // Verifica se o campo id foi utilizado(utilizado apenas para favoritos)
    if (value.id && value.id.length) {
      url += `%20and%20"id"%20IN%20(${value.id.join(',%20')})`;
    }

    // Verifica se o campo Referência foi utilizado
    if (value.reference) {
      url += `%20and%20reference%20ILIKE%20%27%25${value.reference}%25%27`;
    }

    // Verifica se o campo serial number foi utilizado
    if (value.serialNumber) {
      url += `%20and%20device_serial_number%20=%20${value.serialNumber}`;
    }

    // Verifica se o campo Chave Magnética foi utilizado
    if (value.magneticKey != null) {
      url += `%20and%20possible_magnectic_key%20=%20${value.magneticKey}`;
    }

    // Verifica se o campo Bairro foi utilizado
    if (value.district) {
      url += `%20and%20district%20ILIKE%20%27%25${value.district}%25%27`;
    }

    // Verifica se o campo Rua foi utilizado
    if (value.street) {
      url += `%20and%20street%20ILIKE%20%27%25${value.street}%25%27`;
    }

    // Mantém a lógica existente: este bloco sobrescreve a URL inteira
    if (value.circuitBox) {
      url = `%20ILIKE%20%27%25${value.circuitBox}%25%27`;
    }

    return {
      url,
      gatewayIdsToShow:
        value.gateway && value.gateway.length ? value.gateway : undefined,
    };
  }

  filterGatewaysBySearchTerm<T extends { reference: string }>(
    allGateways: T[],
    searchTerm: any,
  ): T[] {
    const term = (searchTerm ?? '').toString().toLowerCase();
    if (!term) return allGateways;
    return allGateways.filter(
      (gateway) => gateway.reference.toLowerCase().indexOf(term) !== -1,
    );
  }

  divideURLSearchParams(
    installations: any[],
    maxSize: number,
  ): URLSearchParams[] {
    const batches = this.chunk(installations, maxSize);

    return batches.map((batch) => {
      const params = new URLSearchParams();
      batch.forEach((item) => {
        const mac = item?.properties?.device_mac;
        if (mac) {
          params.append('mac', mac);
        }
      });
      return params;
    });
  }

  // Função utilizada para agrupar o array de macs em arrays menores
  chunk<T>(items: T[], chunkSize: number): T[][] {
    const size = Math.max(1, Math.floor(chunkSize));
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += size)
      out.push(items.slice(i, i + size));
    return out;
  }

  formatPolygonCoordinates(coordinates: google.maps.LatLng[]): string {
    let coordinateString = coordinates
      .map((coord) => `${coord.lng()} ${coord.lat()}`)
      .join(',');
    const firstCoordinate = coordinates[0];
    coordinateString += `, ${firstCoordinate.lng()} ${firstCoordinate.lat()}`;
    return coordinateString;
  }
}
